import { Input } from '@fluentui/react-components';
import { Search20Regular } from '@fluentui/react-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ColorAvatar } from '../Avatar/ColorAvatar';
import { Button } from '../Button';
import { CommunicationMessage, IllustrationSize, Illustrations } from '../Communication';
import { Flex } from '../Flex';
import { ListSelectionType, VirtualizedList } from '../List';
import { Loader } from '../Loader';
import { ResponsiveDialog } from '../ResponsiveDialog/ResponsiveDialog';
import { ThemeMode, useProviderTheme } from '../hooks/useProviderTheme';
import styles from './GenericPicker.module.css';

export interface DialogTexts {
  header: string;
  confirmBtn: string;
  searchPlaceholder: string;
  emptyFilterHint: string;
  emptyListHint: string;
}

export interface GenericPickerProps<T> {
  open: boolean;
  objects: T[];
  objectLabelKeys: Array<keyof T>;
  objectLabelContentKey?: keyof T;
  objectIndexKey: keyof T;
  filterOptions: Array<keyof T>;
  onObjectSelected: (selectedObjects: T[]) => void;
  onCancelClick: () => void;
  isLoading?: boolean;
  allowMultiselect?: boolean;
  dialogTexts: DialogTexts;
}

/**
 * Dialog for selecting a single object record from a list of objects.
 */
export const GenericPicker = <T,>({
  open,
  objects,
  objectLabelKeys,
  objectLabelContentKey,
  objectIndexKey,
  filterOptions,
  onObjectSelected,
  onCancelClick,
  isLoading = false,
  allowMultiselect = false,
  dialogTexts,
}: GenericPickerProps<T>) => {
  const { t } = useTranslation();
  const theme = useProviderTheme();
  const [filterQuery, setFilterQuery] = useState<string>('');
  const [selectedObjects, setSelectedObjects] = useState<T[]>([]);

  /**
   * Reset filter on each dialog open/close
   */
  useEffect(() => {
    setFilterQuery('');
  }, [open]);

  const filteredObjects = useMemo(() => {
    const expressions = (object: T) =>
      filterOptions.map((property) => {
        const value = object[property];
        if (typeof value === 'string' || typeof value === 'number') {
          return value.toString().toLowerCase().includes(filterQuery.toLowerCase());
        }
        return false;
      });
    return objects.filter((object) =>
      expressions(object).reduce((accumulator, currentValue) => accumulator || currentValue, false),
    );
  }, [objects, filterQuery, filterOptions]);

  const listItems = useMemo(() => {
    const objectLabelKeysToString = (object: T, objectLabelKeys: Array<keyof T>) => {
      let lable = '';
      objectLabelKeys.forEach((key, index) => {
        const spacer = index > 0 ? ' ' : '';
        lable += spacer + object[key];
      });
      return lable;
    };

    return filteredObjects.map((object) => ({
      label: objectLabelKeysToString(object, objectLabelKeys),
      content: objectLabelContentKey ? (object[objectLabelContentKey] as string) : '',
      avatar: <ColorAvatar name={objectLabelKeysToString(object, objectLabelKeys)} />,
    }));
  }, [filteredObjects, objectLabelContentKey, objectLabelKeys]);

  const handleOnSaveClick = () => {
    if (selectedObjects !== null) {
      onObjectSelected(selectedObjects);
    }
  };

  const handleFilterQueryChange = (searchQuery: string) => {
    setFilterQuery(searchQuery);
  };

  const handleSelectionChange = (selected: number[]) => {
    const selectedPersons = selected.map((index) => filteredObjects[index]);
    setSelectedObjects(selectedPersons);
  };

  const selectedIndizes = useMemo(() => {
    const items = selectedObjects
      .map<number>((item) =>
        filteredObjects.findIndex((a) => a[objectIndexKey] === item[objectIndexKey]),
      )
      .filter((number) => number !== -1) as number[];
    return items;
  }, [selectedObjects, filteredObjects, objectIndexKey]);

  return (
    <ResponsiveDialog
      open={open}
      className={styles.genericPicker}
      header={dialogTexts.header}
      primaryAction={
        <Button
          appearance="primary"
          disabled={selectedObjects.length === 0}
          onClick={handleOnSaveClick}
        >
          {dialogTexts.confirmBtn}
        </Button>
      }
      secondaryAction={
        <Button onClick={onCancelClick}>{t('cancel', { defaultValue: 'Abbrechen' })}</Button>
      }
    >
      <Flex direction="column" align="stretch" fill gap="0.5rem" className="wrapper">
        <Input
          appearance="filled-darker"
          contentBefore={<Search20Regular />}
          placeholder={dialogTexts.searchPlaceholder}
          onChange={(_, data) => (data ? handleFilterQueryChange(data?.value) : null)}
        />
        {!isLoading && objects.length > 0 && (
          <>
            {listItems.length > 0 && (
              <VirtualizedList
                entries={listItems}
                className="list"
                selectionType={
                  allowMultiselect ? ListSelectionType.Multi : ListSelectionType.Single
                }
                selectedEntries={selectedIndizes}
                onSelectionChange={handleSelectionChange}
              />
            )}
            {listItems.length === 0 && (
              <div className="list">
                <CommunicationMessage
                  illustration={Illustrations.Empty}
                  illustrationSize={IllustrationSize.Small}
                  headline={dialogTexts.emptyFilterHint}
                  text=""
                  useDarkIllustration={theme.mode === ThemeMode.Dark}
                />
              </div>
            )}
          </>
        )}
        {!isLoading && objects.length === 0 && (
          <div className="list">
            <CommunicationMessage
              illustration={Illustrations.Empty}
              illustrationSize={IllustrationSize.Small}
              headline={dialogTexts.emptyListHint}
              text=""
              useDarkIllustration={theme.mode === ThemeMode.Dark}
            />
          </div>
        )}
        {isLoading && (
          <Flex className="list" align="center" justify="center">
            <Loader />
          </Flex>
        )}
      </Flex>
    </ResponsiveDialog>
  );
};
